import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import clsx from 'clsx'
import {
  IconArrowDown,
  IconArrowUp,
  IconPoint,
  IconStar,
} from '@tabler/icons-react'
import TeamStat from '../../stories/TeamStat/TeamStat'
import LastFiveComponent from '../../stories/LastFive/LastFive'
import ClaireCard from '../../stories/ClaireCard/ClaireCard'
import classes from './TeamComponents.module.css'
import { useTeam, useTeamStats, useTeamPlayersOptional } from '../api'
import { usePlayerDetailsOptional } from '../../players/api'
import { getCode } from 'country-list'
import * as flags from 'country-flag-icons/react/3x2'
import Stat from '../../stories/Stat/Stat'
import KpiList from '../../widgets/components/KpiList'
import ProgressBox from '../../widgets/components/ProgressBox'
import PerformanceSection from '../../common/components/metrics/PerformanceSection'
import { useParams } from 'react-router'
import { Typography } from '../../common/typography'
import { useMonthKPIs } from '../../widgets/hooks'
import { getInsight } from '../../performance/insights'
import { useCompetitionId } from '../../common/hooks'
import { WidgetContextProvider } from '../../widgets/providers'
import { useTeamRanks } from '../../ranks/api'
import TeamCaptainBox from './TeamCaptainBox'
import RaceRankDisplay from './RaceRankDisplay'
import MatchRankDisplay from './MatchRankDisplay'
import { useTeamRaceStats } from '../../races/api'
import { getRankSuffix } from '../../common/helpers'
import {
  useActiveCompetitions,
  useCompetitionDetails,
} from '../../navigation/api'
import { useLearningStatsOptional } from '../../learning/api'
import LearningLevel from '../../learning/components/LearningLevel'
import { useState } from 'react'
import Breakdown from '../../stories/Breakdown/Breakdown'

function TeamMatchStats({
  competition_id,
  team_id,
}: {
  competition_id: number
  team_id: number
}) {
  const { data: team_match_stats } = useTeamStats({
    competition_id,
    team_id: team_id,
  })

  return (
    <div className={classes.form_stats_row}>
      <div className={classes.formContainer}>
        <Typography fw={500} lh="125">
          Form
        </Typography>
        <LastFiveComponent last_five={team_match_stats.last_5_matches} />
      </div>

      <div className={classes.team_statistics_container}>
        <TeamStat title="Played" value={team_match_stats.total_matches} />
        <TeamStat title="Won" value={team_match_stats.matches_won} />
        <TeamStat title="Draw" value={team_match_stats.matches_draw} />
        <TeamStat title="Lost" value={team_match_stats.matches_lost} />
        <TeamStat title="GF" value={team_match_stats.goals_for} />
        <TeamStat title="GA" value={team_match_stats.goals_against} />
        <TeamStat title="GD" value={team_match_stats.goal_difference} />
      </div>
    </div>
  )
}

function TeamRaceStats({
  competition_id,
  team_id,
}: {
  competition_id: number
  team_id: number
}) {
  const { data: team_race_stats } = useTeamRaceStats({
    competition_id: competition_id,
    team_id: team_id,
  })
  return (
    <div className={classes.form_stats_row}>
      <div className={classes.formContainer}>
        <Typography fw={500} lh="125">
          Form
        </Typography>
        {team_race_stats.recent_races.slice(0, 5).map((race) => (
          <div
            className={clsx(
              classes.result,
              { [classes.firstPlace]: Number(race.result_rank) === 1 },
              { [classes.secondPlace]: Number(race.result_rank) === 2 },
              { [classes.thirdPlace]: Number(race.result_rank) === 3 }
            )}
          >
            <Typography
              fs="xs"
              fc={{ light: 'white', dark: 'white' }}
              ta="center"
              ff="JetBrains Mono"
              lh="150"
            >
              {race.result_rank}
              <sup style={{ fontFamily: 'Barlow' }}>
                {getRankSuffix(race.result_rank)}
              </sup>
            </Typography>
          </div>
        ))}
      </div>

      <div className={classes.team_statistics_container}>
        <TeamStat title="Starts" value={team_race_stats.season_stats.starts} />
        <TeamStat title="Wins" value={team_race_stats.season_stats.wins} />
        <TeamStat title="Top 3" value={team_race_stats.season_stats.top_3} />
        <TeamStat title="Top 5" value={team_race_stats.season_stats.top_5} />
        <TeamStat
          title="Avg Race Rating"
          value={team_race_stats.season_stats.avg_race_score}
          decimals={2}
        />
      </div>
    </div>
  )
}

export default function TeamOverview() {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const { teamId } = useParams()
  const competition_id = useCompetitionId()
  const { data: activeCompetitionsData } = useActiveCompetitions()
  const hasLearningModule =
    activeCompetitionsData?.platform_features.has_learning_module ?? false
  const competitionDetails = useCompetitionDetails({
    competition_id: competition_id,
  })
  const [
    pointBreakdownOpen,
    { open: openPointBreakdown, close: closePointBreakdown },
  ] = useDisclosure(false)
  const [subject, setSubject] = useState<'rating' | 'total-points'>('rating')

  const { data: team } = useTeam({
    competition_id,
    team_id: Number(teamId),
  })

  const { data: teamRanksResponse } = useTeamRanks({
    competition_id,
    team_id: Number(teamId),
  })

  if (teamRanksResponse.items.length === 0) {
    return (
      <div className={classes.noDataContainer}>
        <Typography
          fs="md"
          fc={{ light: 'gray-8', dark: 'gray-2' }}
          ta="center"
        >
          Currently there is no data available for team:
        </Typography>
        <Typography
          fs="md"
          fw={700}
          fc={{ light: 'gray-8', dark: 'gray-2' }}
          ta="center"
        >{`${team.name}`}</Typography>
      </div>
    )
  }

  const teamRank = teamRanksResponse.items[0]

  const isSinglePlayerTeam =
    competitionDetails.data?.single_player_teams ?? false

  const { data: teamPlayers } = useTeamPlayersOptional({
    competition_id,
    team_id: Number(teamId),
    enabled: isSinglePlayerTeam,
  })

  const { data: teamLearningStats } = useLearningStatsOptional({
    team_id: Number(teamId),
    enabled: hasLearningModule,
  })

  const singlePlayerId =
    isSinglePlayerTeam && teamPlayers && teamPlayers.length > 0
      ? teamPlayers[0].player_id
      : null

  const { data: singlePlayer } = usePlayerDetailsOptional({
    competition_id,
    player_id: singlePlayerId ?? 0,
    enabled: isSinglePlayerTeam && singlePlayerId !== null,
  })

  const playerFields = singlePlayer?.fields as
    | Record<string, string | number | boolean>
    | undefined
  const hasCountry = playerFields && 'country' in playerFields
  const hasPosition = playerFields && 'position' in playerFields
  const countryCode =
    hasCountry && typeof playerFields.country === 'string'
      ? (getCode(playerFields.country)?.toUpperCase() ?? null)
      : null

  const { data: month } = useMonthKPIs(competition_id, true, {
    subject_type: 'team',
    subject_ids: teamId,
  })
  const insightResult = getInsight(month, false)

  const getRankChangeIcon = (change: number) => {
    if (change > 0) return IconArrowUp
    if (change < 0) return IconArrowDown
    return IconPoint
  }

  const getRankChangeColor = (change: number) => {
    if (change > 0) return 'green'
    if (change < 0) return 'red'
    return 'gray'
  }

  function handleStatClick(subject: 'rating' | 'total-points') {
    setSubject(subject)
    openPointBreakdown()
  }

  return (
    <div className={classes.overview_container}>
      <div className={classes.overview_stat_widgets_wrapper}>
        <>
          {isSinglePlayerTeam ? (
            (hasPosition ||
              countryCode ||
              (teamRank.division_name &&
                teamRank.division_name.length > 0)) && (
              <div className={classes.overview_stat_row}>
                {hasPosition && (
                  <Stat
                    title="Role Position"
                    value={playerFields?.position as string}
                    background="white"
                  />
                )}
                {countryCode &&
                  flags[countryCode as keyof typeof flags] &&
                  (() => {
                    const FlagComponent =
                      flags[countryCode as keyof typeof flags]
                    return (
                      <div className={classes.statBox}>
                        <Typography fw={500} lh="130" ta="center">
                          Country
                        </Typography>
                        <FlagComponent className={classes.flagIcon} />
                      </div>
                    )
                  })()}
                {teamRank.division_name &&
                  teamRank.division_name.length > 0 && (
                    <Stat
                      title="Division"
                      value={teamRank.division_name}
                      background="white"
                    />
                  )}
              </div>
            )
          ) : (
            <>
              {/* Mobile without captain: combine # Players and Division in one row */}
              {!isDesktop &&
              !team.captain &&
              teamRank.division_name &&
              teamRank.division_name.length > 0 ? (
                <div className={classes.overview_stat_row}>
                  <Stat
                    title="# Players"
                    value={team.player_count}
                    background="white"
                  />
                  <Stat
                    title="Division"
                    value={teamRank.division_name}
                    background="white"
                  />
                </div>
              ) : (
                <>
                  <div className={classes.overview_stat_row}>
                    {team.captain && (
                      <TeamCaptainBox
                        name={team.captain.name}
                        photo={team.captain.main_photo_url}
                      />
                    )}
                    <Stat
                      title="# Players"
                      value={team.player_count}
                      background="white"
                    />
                  </div>
                  {teamRank.division_name &&
                    teamRank.division_name.length > 0 && (
                      <div className={classes.overview_stat_row}>
                        <Stat
                          title="Division"
                          value={teamRank.division_name}
                          background="white"
                        />
                      </div>
                    )}
                </>
              )}
            </>
          )}
        </>

        {!isDesktop && (
          <div className={classes.overview_stat_row}>
            <Stat
              title="Overall Rank"
              value={teamRank?.rank ?? null}
              icon={getRankChangeIcon(teamRank?.rank_change ?? 0)}
              iconColor={getRankChangeColor(teamRank?.rank_change ?? 0)}
              isValueSub
              isTeamHeader
              background="white"
            />
            {competitionDetails.data?.kind === 'race' ? (
              <RaceRankDisplay
                competitionId={competition_id}
                teamId={Number(teamId)}
              />
            ) : (
              <MatchRankDisplay
                leagueRank={teamRank?.league_rank}
                leagueRankChange={teamRank?.league_rank_change}
              />
            )}
            <Stat
              title="Rating"
              value={teamRank?.rating ? teamRank.rating.toFixed(2) : null}
              icon={IconStar}
              background="white"
              onClick={() => handleStatClick('rating')}
            />
          </div>
        )}

        <div className={classes.overview_stat_row}>
          <Stat
            title="Total Points"
            value={Number(teamRank?.points.toFixed(2))}
            background="white"
            onClick={() => handleStatClick('total-points')}
          />
          {teamLearningStats && (
            <LearningLevel
              level={teamLearningStats.xp_level}
              component="team"
            />
          )}
        </div>
      </div>
      {competitionDetails.data && competitionDetails.data.kind === 'match' ? (
        <TeamMatchStats
          competition_id={competition_id}
          team_id={Number(teamId)}
        />
      ) : (
        <TeamRaceStats
          competition_id={competition_id}
          team_id={Number(teamId)}
        />
      )}
      <div
        className={clsx(
          { [classes.overview_widgets_wrapper]: isDesktop },
          { [classes.overview_widgets_wrapper_mobile]: !isDesktop }
        )}
      >
        <div className={classes.statsContainer}>
          <Typography fw={500} lh="100">
            YTD Stats
          </Typography>
          <KpiList id="kpis" />
        </div>
        <ClaireCard title="Insights" text={insightResult.text} />

        <div className={classes.stat_cards}>
          <WidgetContextProvider
            context={{ metric: insightResult.topPerformer?.key }}
          >
            <ProgressBox id="insights0" />
          </WidgetContextProvider>
          <WidgetContextProvider
            context={{ metric: insightResult.bottomPerformer?.key }}
          >
            <ProgressBox id="insights1" />
          </WidgetContextProvider>
        </div>
      </div>

      <PerformanceSection />
      <Breakdown
        opened={pointBreakdownOpen}
        close={closePointBreakdown}
        competitionId={competition_id}
        teamId={team.team_id}
        subject={subject}
      />
    </div>
  )
}
